import React from 'react';
import styled from '@emotion/styled';
import { useLocale } from 'locale';

export enum RenewalProgramStatus {
  DepositedInitial,
  DepositedAdvanced,
  Approved,
  PermitRequested,
  ConditionalPermit,
  PermitGranted,
}

const STEPS_COUNT = 6;

const RENEWAL_STATUS_COLORS: Record<RenewalProgramStatus, string> = {
  [RenewalProgramStatus.DepositedInitial]: '#9493E8',
  [RenewalProgramStatus.DepositedAdvanced]: '#007AFF',
  [RenewalProgramStatus.Approved]: '#00D2B4',
  [RenewalProgramStatus.PermitRequested]: '#C7E24F',
  [RenewalProgramStatus.ConditionalPermit]: '#FFD700',
  [RenewalProgramStatus.PermitGranted]: '#FF8C00',
};

const getStatusLabel = (status: RenewalProgramStatus, t: (key: any) => string): string => {
  switch (status) {
    case RenewalProgramStatus.DepositedInitial:
      return t('investorListingCard.renewalStatus.depositedInitial');
    case RenewalProgramStatus.DepositedAdvanced:
      return t('investorListingCard.renewalStatus.depositedAdvanced');
    case RenewalProgramStatus.Approved:
      return t('investorListingCard.renewalStatus.approved');
    case RenewalProgramStatus.PermitRequested:
      return t('investorListingCard.renewalStatus.permitRequested');
    case RenewalProgramStatus.ConditionalPermit:
      return t('investorListingCard.renewalStatus.conditionalPermit');
    case RenewalProgramStatus.PermitGranted:
      return t('investorListingCard.renewalStatus.permitGranted');
    default:
      return '';
  }
};

const PillWrapper = styled.div<{ small?: boolean; smallSpace?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ small, theme }) => small ? '4px' : theme.spacing(1)};
  border: 1px solid ${({ theme }) => theme.colors.neutrals.grey6};
  border-radius: ${({ theme }) => theme.borderRadius.smallestSideRound};
  background-color: ${({ theme }) => theme.colors.neutrals.white};
  padding: ${({ small, theme, smallSpace }) => small ? '1px 6px' : (smallSpace ? `${theme.spacing(0.25)} ${theme.spacing(0.75)}` : `${theme.spacing(0.75)} ${theme.spacing(1.25)}`)};
  align-self: flex-start;
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const PillLabel = styled.span<{ small?: boolean }>`
  font-size: ${({ small }) => small ? '9px' : '13px'};
  line-height: 20px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.neutrals.grey1};
  white-space: nowrap;
`;

const DotsContainer = styled.div<{ small?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ small }) => small ? '1px' : '2px'};
  direction: ltr;
  flex-shrink: 0;
`;

const DotCircle = styled.span<{ isActive: boolean; dotColor: string; small?: boolean }>`
  display: inline-block;
  width: ${({ small }) => small ? '9px' : '12px'};
  height: ${({ small }) => small ? '9px' : '12px'};
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ isActive, dotColor, theme }) =>
    isActive ? dotColor : theme.colors.neutrals.grey7};
`;

interface RenewalProgramIndicatorProps {
  status: number;
  small?: boolean;
  smallSpace?: boolean;
}

export const RenewalProgramIndicator: React.FC<RenewalProgramIndicatorProps> = ({ status, small, smallSpace }) => {
  const { t } = useLocale();

  const activeStep: RenewalProgramStatus = status;
  const dotColor = RENEWAL_STATUS_COLORS[activeStep];
  const label = getStatusLabel(activeStep, t);

  const activeDots = Math.min(status + 1, STEPS_COUNT);
  const activeFrom = STEPS_COUNT - activeDots;

  return (
    <PillWrapper small={small} aria-label={label} smallSpace={smallSpace} data-auto="urban-renewal-indicator">
      <DotsContainer small={small} aria-hidden="true">
        {Array.from({ length: STEPS_COUNT }, (_, i) => (
          <DotCircle
            key={i}
            small={small}
            isActive={i >= activeFrom}
            dotColor={dotColor}
          />
        ))}
      </DotsContainer>
      <PillLabel small={small} aria-hidden="true">{label}</PillLabel>
    </PillWrapper>
  );
};
